import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { Symptom } from 'src/app/shared/symptoms/symptom.model';
import { ClinicSetupService } from '../../clinic-setup.service';
import { Layout, LayoutsService } from './../layouts.service';
import { CanHideSignPipe } from './can-hide-sign.pipe';

@Component({
  selector: 'csi-signs-layout',
  templateUrl: './signs-layout.component.html',
  styleUrls: ['./signs-layout.component.scss']
})
export class SignsLayoutComponent implements OnInit, OnChanges {
  @Input() signsLayoutFormControl: FormControl;
  @Input() isSaving: boolean;
  @Input() layout: Layout;
  @Input() layoutSetting: string;
  @Input() preventHide: boolean;
  @Input() maxShow: number | null = null;
  @Input() preventShowOverride: string[] = [];
  @Input() availableSignKeys: string[] | null = null;

  public isClinicAdmin: boolean;

  // Signs have been named as symptoms
  public signsMap = this.schemaService.symptomMap;

  // Needed for html disabled check
  public manualTrigger: number;
  public touchEnabled = false;

  hideSignKeys: string[] = [];
  showSignKeys: string[] = [];

  hideSearchTerm = '';
  showSearchTerm = '';

  private _initialized = false;
  private _subscriptionPreventShow: string[] = [];

  constructor(
    private schemaService: SchemaService,
    public clinicSetupService: ClinicSetupService,
    public canHideSignPipe: CanHideSignPipe,
    private layoutsService: LayoutsService
  ) {}

  get filteredHideSignKeys(): string[] {
    if (!this.hideSearchTerm) {
      return this.hideSignKeys;
    }
    const term = this.hideSearchTerm.toLowerCase();
    return this.hideSignKeys.filter(key => {
      const name = this.signsMap[key] && this.signsMap[key].name ? this.signsMap[key].name : '';
      return name.toLowerCase().includes(term);
    });
  }

  get filteredShowSignKeys(): string[] {
    if (!this.showSearchTerm) {
      return this.showSignKeys;
    }
    const term = this.showSearchTerm.toLowerCase();
    return this.showSignKeys.filter(key => {
      const name = this.signsMap[key] && this.signsMap[key].name ? this.signsMap[key].name : '';
      return name.toLowerCase().includes(term);
    });
  }

  get isSearchActive(): boolean {
    return !!(this.hideSearchTerm || this.showSearchTerm);
  }

  ngOnInit() {
    this.layout = { ...this.layout };
    this.signsLayoutFormControl.setValue(this.layout);
    this.isClinicAdmin = this.clinicSetupService.isClinicAdmin;

    const layoutData = this.layout[this.layoutSetting];
    this.showSignKeys = layoutData ? [...layoutData.show] : [];

    const layoutField = this.layout[this.layoutSetting] || {};
    this._subscriptionPreventShow = [...(layoutField.preventShow || [])];

    this.showSignKeys = [...this.layout[this.layoutSetting].show];
    const allKeys = this.availableSignKeys || this.schemaService.symptomKeys;
    this.hideSignKeys = allKeys.filter(
      symptomKey => !this.showSignKeys.includes(symptomKey)
    );

    if (this.maxShow != null && this.showSignKeys.length > this.maxShow) {
      const overflow = this.showSignKeys.splice(this.maxShow);
      this.hideSignKeys = [...overflow, ...this.hideSignKeys];
      this.setFormControl();
    }

    this.touchEnabled = this.layoutsService.isTouchEnabled();
    this._initialized = true;

    if (this.preventShowOverride.length > 0) {
      this.applyPreventShowOverride();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.preventShowOverride && this._initialized) {
      this.applyPreventShowOverride();
    }
  }

  private applyPreventShowOverride() {
    const combined = [...this._subscriptionPreventShow, ...this.preventShowOverride];
    this.layout[this.layoutSetting].preventShow = combined;

    const toMove = this.showSignKeys.filter(key => combined.includes(key));
    for (const key of toMove) {
      const idx = this.showSignKeys.indexOf(key);
      if (idx > -1) {
        this.showSignKeys.splice(idx, 1);
        this.hideSignKeys.push(key);
      }
    }
    if (toMove.length > 0) {
      this.setFormControl();
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.setFormControl();
  }

  canBeHiddenPredicate(item: CdkDrag<[string, Symptom, boolean]>, list: CdkDropList) {
    const canHideSignPipe = new CanHideSignPipe();
    return canHideSignPipe.transform(
      item.data[1],
      item.data[0],
      this.showSignKeys, // Use the component property directly
      item.data[2]
    );
  }

  canBeShownPredicate() {
    const signsLayout = this.layout && this.layout.signsLayout;
    const preventShow = signsLayout ? [...(signsLayout.preventShow || [])] : [];
    if (!preventShow.length) return () => true;

    return (item: CdkDrag<string>) => {
      if (preventShow.includes(item.data)) {
        return false;
      }
      return this.maxShow == null || this.showSignKeys.length < this.maxShow;
    };
  }

  setVisibility(signKey: string, action: 'show' | 'hide') {
    if (action === 'show' && this.maxShow != null && this.showSignKeys.length >= this.maxShow) {
      return;
    }
    const [sourceArray, targetArray] =
      action === 'hide'
        ? [this.showSignKeys, this.hideSignKeys]
        : [this.hideSignKeys, this.showSignKeys];
    const index = sourceArray.indexOf(signKey);
    if (index === -1) {
      return;
    }
    transferArrayItem(sourceArray, targetArray, index, targetArray.length);
    this.manualTrigger = Math.random();
    this.setFormControl();
  }

  reorder(signKey: string, direction: 'up' | 'down') {
    const index = this.showSignKeys.indexOf(signKey);
    if (index === -1) {
      return;
    }
    moveItemInArray(this.showSignKeys, index, direction === 'up' ? index - 1 : index + 1);
    this.setFormControl();
  }

  setFormControl() {
    const currentLayout = this.signsLayoutFormControl.value || {};
    const currentSignsLayout = currentLayout[this.layoutSetting] || {};
    this.layoutsService.updateLayoutFormControlValue(
      this.signsLayoutFormControl,
      this.layoutSetting,
      {
        ...currentSignsLayout,
        show: this.showSignKeys,
        hide: this.hideSignKeys
      }
    );
  }
}
